import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { Card, CardContent } from '../../components/ui/card';
import { formatDistanceToNow } from 'date-fns';
import { Check, X, AlertTriangle, Clock, Database, BrainCircuit, Zap, RefreshCw, Star } from 'lucide-react';
import { useToast } from '../../components/ui/use-toast';
import type { ExecutionLog } from '../../types/agent-types-new';

/**
 * Renders one execution log exactly as fetched. A field the row did not record is
 * shown as "not recorded"; a fetch failure is shown as the failure. The previous
 * version "enriched" the log with mock data for UI demonstration (4.36s, 1,245
 * tokens, gpt-4o, an invented SEO report) and labelled a missing status 'success'.
 */
interface LogDetailDialogProps {
  log: ExecutionLog | null;
  isOpen: boolean;
  onClose: () => void;
  isLoading?: boolean;
  /** The real error from the detail fetch; rendered instead of a log. */
  error?: string | null;
}

const NotRecorded = () => <span className="text-muted-foreground">not recorded</span>;

const seconds = (v: unknown) => (typeof v === 'number' ? `${v.toFixed(2)}s` : null);

const statusColour = (status: string) => {
  switch (status) {
    case 'success':
    case 'completed':
      return 'bg-green-500';
    case 'failed':
    case 'error':
      return 'bg-red-500';
    case 'timeout':
      return 'bg-amber-500';
    case 'processing':
      return 'bg-blue-500';
    default:
      return 'bg-gray-400';
  }
};

const statusIcon = (status: string) => {
  switch (status) {
    case 'success':
    case 'completed':
      return <Check className="h-4 w-4 text-green-500" />;
    case 'failed':
    case 'error':
      return <X className="h-4 w-4 text-red-500" />;
    case 'timeout':
      return <AlertTriangle className="h-4 w-4 text-amber-500" />;
    default:
      return null;
  }
};

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

const relative = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : formatDistanceToNow(d, { addSuffix: true });
};

// Raw Output is the recorded value, serialised whole. Parsed Output unwraps a lone
// string `result` for readability, and only a lone one: any sibling field (an
// `error` beside a partial answer, say) keeps the whole object on screen.
const renderOutput = (value: unknown, mode: 'parsed' | 'raw') => {
  if (value == null || value === '') return <NotRecorded />;
  if (typeof value === 'string') return <pre className="text-sm whitespace-pre-wrap">{value}</pre>;
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (mode === 'parsed' && typeof obj.result === 'string' && Object.keys(obj).length === 1) {
      return <pre className="text-sm whitespace-pre-wrap">{obj.result}</pre>;
    }
    return <pre className="text-xs whitespace-pre-wrap">{JSON.stringify(value, null, 2)}</pre>;
  }
  return <pre className="text-sm whitespace-pre-wrap">{String(value)}</pre>;
};

const EnhancedLogDetailDialog: React.FC<LogDetailDialogProps> = ({ log, isOpen, onClose, isLoading = false, error = null }) => {
  const { toast } = useToast();

  if (!isOpen) return null;

  if (error) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              Log detail could not be fetched
            </DialogTitle>
            <DialogDescription>The request failed; nothing is shown in its place.</DialogDescription>
          </DialogHeader>
          <pre className="text-xs whitespace-pre-wrap bg-red-50 border border-red-200 text-red-800 p-3 rounded-md" role="alert">{error}</pre>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  if (isLoading || !log) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Loading Log Details</DialogTitle>
            <DialogDescription>Fetching the execution record...</DialogDescription>
          </DialogHeader>
          <div className="flex justify-center items-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-primary" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const status = log.status || 'unknown';
  const input = (log.input && typeof log.input === 'object') ? (log.input as Record<string, unknown>) : null;
  const metadata = (log.metadata && typeof log.metadata === 'object') ? log.metadata : null;
  const timing = (log.timing_breakdown && typeof log.timing_breakdown === 'object') ? (log.timing_breakdown as Record<string, unknown>) : null;


  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            {statusIcon(status)}
            <span>Execution Log Details</span>
            <Badge className={`ml-2 ${statusColour(status)}`}>{status}</Badge>
            {log.agent_type ? <Badge variant="outline" className="ml-2">{log.agent_type}</Badge> : null}
          </DialogTitle>
          <DialogDescription className="flex items-center flex-wrap mt-2">
            {log.timestamp ? (
              <>
                <span>{formatDate(log.timestamp)}</span>
                <span className="mx-2">•</span>
                <span>{relative(log.timestamp)}</span>
              </>
            ) : (
              <span>Timestamp <NotRecorded /></span>
            )}
            {log.traceId && (
              <>
                <span className="mx-2">•</span>
                <span className="font-mono">ID: {log.traceId}</span>
              </>
            )}
          </DialogDescription>
          {log.error && (
            <pre className="mt-2 p-2 text-xs rounded-md bg-red-50 border border-red-200 text-red-800 whitespace-pre-wrap" role="alert">{log.error}</pre>
          )}
        </DialogHeader>

        <div className="space-y-6 py-2">
          <div>
            <h4 className="text-sm font-medium mb-2">Input</h4>
            <Card>
              <CardContent className="p-4">
                {input ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><span className="text-muted-foreground">Page ID:</span> {input.pageId ? String(input.pageId) : <NotRecorded />}</div>
                      <div><span className="text-muted-foreground">Session ID:</span> {input.sessionId ? String(input.sessionId) : <NotRecorded />}</div>
                    </div>
                    {typeof input.customerMessage === 'string' && input.customerMessage !== '' && (
                      <div>
                        <div className="text-muted-foreground text-sm mb-1">Customer Message:</div>
                        <div className="bg-muted p-3 rounded-md text-sm">{input.customerMessage}</div>
                      </div>
                    )}
                    <Collapsible className="w-full">
                      <div className="flex w-full justify-between items-center p-2 border rounded-md">
                        <CollapsibleTrigger className="text-left font-medium text-sm">Page Content</CollapsibleTrigger>
                        <span className="text-xs text-muted-foreground">Click to view</span>
                      </div>
                      <CollapsibleContent>
                        <div className="bg-muted p-3 rounded-md mt-2 max-h-40 overflow-y-auto">
                          {typeof input.pageContent === 'string' && input.pageContent !== ''
                            ? <pre className="text-xs whitespace-pre-wrap">{input.pageContent}</pre>
                            : <NotRecorded />}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                ) : (
                  <div className="text-sm">Input <NotRecorded /></div>
                )}
              </CardContent>
            </Card>
          </div>

          <div>
            <h4 className="text-sm font-medium mb-2">Model Configuration</h4>
            <div className="flex flex-wrap gap-2 mb-4 text-sm">
              <div className="px-2 py-1 bg-muted rounded-md">Model: <span className="font-medium">{metadata?.model || metadata?.llm_model ? String(metadata.model || metadata.llm_model) : <NotRecorded />}</span></div>
              <div className="px-2 py-1 bg-muted rounded-md">Temperature: <span className="font-medium">{typeof metadata?.temperature === 'number' ? metadata.temperature : <NotRecorded />}</span></div>
              <div className="px-2 py-1 bg-muted rounded-md">Max Tokens: <span className="font-medium">{typeof metadata?.max_tokens === 'number' ? metadata.max_tokens : <NotRecorded />}</span></div>
              <div className="px-2 py-1 bg-muted rounded-md">Used Tokens: <span className="font-medium">{typeof log.tokenUsage === 'number' ? log.tokenUsage.toLocaleString() : <NotRecorded />}</span></div>
              <div className="px-2 py-1 bg-muted rounded-md">Duration: <span className="font-medium">{seconds(log.duration) ?? <NotRecorded />}</span></div>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium mb-2">Timing Breakdown</h4>
            {timing ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {([
                  ['database', 'Database', <Database key="db" className="h-5 w-5 text-blue-500 mb-1" />],
                  ['rag', 'RAG', <BrainCircuit key="rag" className="h-5 w-5 text-purple-500 mb-1" />],
                  ['llm', 'LLM', <Zap key="llm" className="h-5 w-5 text-amber-500 mb-1" />],
                  ['total', 'Total', <Clock key="total" className="h-5 w-5 text-green-500 mb-1" />],
                ] as const).map(([key, label, icon]) => (
                  <Card key={key} className="flex-1">
                    <CardContent className="p-4 flex flex-col items-center justify-center">
                      {icon}
                      <div className="font-medium">{seconds(timing[key]) ?? <NotRecorded />}</div>
                      <div className="text-xs text-muted-foreground">{label}</div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-sm">Timing breakdown <NotRecorded /></div>
            )}
          </div>

          <div>
            <h4 className="text-sm font-medium mb-2">Prompt</h4>
            {log.prompt ? (
              <Collapsible className="w-full">
                <div className="flex w-full justify-between items-center p-2 border rounded-md">
                  <CollapsibleTrigger className="text-left font-medium text-sm">View prompt</CollapsibleTrigger>
                </div>
                <CollapsibleContent>
                  <pre className="bg-muted p-3 rounded-md mt-2 text-xs whitespace-pre-wrap max-h-60 overflow-y-auto">{log.prompt}</pre>
                </CollapsibleContent>
              </Collapsible>
            ) : (
              <div className="text-sm">Prompt <NotRecorded /></div>
            )}
          </div>

          <div>
            <h4 className="text-sm font-medium mb-2">Result</h4>
            <Tabs defaultValue="formatted">
              <TabsList>
                <TabsTrigger value="formatted">Parsed Output</TabsTrigger>
                <TabsTrigger value="raw">Raw Output</TabsTrigger>
              </TabsList>
              <TabsContent value="formatted" className="mt-0">
                <Card><CardContent className="p-4">{renderOutput(log.parsed_output, 'parsed')}</CardContent></Card>
              </TabsContent>
              <TabsContent value="raw" className="mt-0">
                <Card><CardContent className="p-4">{renderOutput(log.raw_output, 'raw')}</CardContent></Card>
              </TabsContent>
            </Tabs>
          </div>

          <div>
          {/*
            A "Rate this execution" trainer form used to sit below this block (task-flqblaa1r2).
            It called agentService.rateAgentExecution -> agentApiFixed.rateAgentExecution, which was
            a MOCK: a Math.random sleep returning { success: true } with no HTTP call. So the form
            toasted "Feedback submitted" for every click, and its else/catch branches -- "Submission
            failed" and the real error -- COULD NOT FIRE, because the mock could neither return false
            nor throw. Error handling that cannot fire is indistinguishable from error handling that
            works, which is why it survived review. There is no rating route and no agent_ratings
            table, so it was removed rather than wired: see the PR for the measurement.
          */}
            <h4 className="text-sm font-medium mb-2">Customer Feedback</h4>
            <Card>
              <CardContent className="p-4">
                {log.customerFeedback ? (
                  <div className="space-y-2">
                    <div className="flex items-center">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star key={i} className={`h-4 w-4 ${i <= log.customerFeedback!.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                      ))}
                      <span className="ml-2 text-sm">{log.customerFeedback.rating}/5</span>
                    </div>
                    {log.customerFeedback.comment && <div className="text-sm bg-muted p-2 rounded">{log.customerFeedback.comment}</div>}
                  </div>
                ) : (
                  <div className="text-sm">Customer feedback <NotRecorded /></div>
                )}
              </CardContent>
            </Card>
          </div>

        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EnhancedLogDetailDialog;
